import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useCustomReducer } from '../../base/customHooks'
import {
  CardConsentApi,
  ICardConsentView,
  CONSENT_STATUS_LINK_SENT,
  CONSENT_STATUS_CONSENTED,
} from './services/cardConsentApi'

interface IState {
  loading: boolean
  view: ICardConsentView | null
  accepted: boolean
  busy: boolean
  approvedPhone: string
  code: string
  error: string
}

/**
 * Son kullanıcının SMS ile gelen bağlantıdan ulaştığı onay sayfası.
 * Oturum gerektirmez; bu yüzden route withLogin ile sarmalanmaz.
 * Onay metni, tutar ve kart maskesi YALNIZCA bağlantı hâlâ geçerliyken gösterilir —
 * ödeme sonrası aynı bağlantı açıldığında içerik görünmez.
 */
function CardConsent() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useCustomReducer<IState>({
    loading: true,
    view: null,
    accepted: false,
    busy: false,
    approvedPhone: '',
    code: '',
    error: '',
  })

  useEffect(() => {
    const load = async () => {
      try {
        const res = await new CardConsentApi({}).get(token as string)
        setState({ loading: false, view: res })
      } catch (err) {
        setState({ loading: false, view: null })
      }
    }
    load()
  }, [token])

  const approve = async () => {
    setState({ busy: true, error: '' })
    try {
      const res = await new CardConsentApi({}).approve(token as string)
      if (res) setState({ approvedPhone: res.MaskedPhone, code: res.Code })
    } catch (err: any) {
      setState({ error: err && err.description ? err.description : 'Bu bağlantı artık geçerli değil.' })
    } finally {
      setState({ busy: false })
    }
  }

  if (state.loading) {
    return <div style={{ padding: 24 }}>Yükleniyor…</div>
  }

  // Onay verildikten sonra beyan metni ve işlem detayı GÖSTERİLMEZ; yalnız kod kalır.
  if (state.approvedPhone) {
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
        <p>Onayınız alındı.</p>

        <p>Aşağıdaki doğrulama kodunu bayiye iletiniz:</p>

        <div
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '0.5rem',
            padding: '1rem',
            textAlign: 'center',
            border: '1px solid #ccc',
            borderRadius: 8,
          }}
        >
          {state.code}
        </div>

        <p className='text-muted mt-3'>
          Bu kod olmadan ödeme işlemi tamamlanamaz. Sayfayı kapatmadan önce kodu bayiye iletiniz.
        </p>
      </div>
    )
  }

  if (!state.view || state.view.Status !== CONSENT_STATUS_LINK_SENT) {
    const message =
      state.view && state.view.Status === CONSENT_STATUS_CONSENTED && state.view.Message
        ? state.view.Message
        : 'Bu bağlantı artık geçerli değil.'

    return <div style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>{message}</div>
  }

  const view = state.view

  return (
    <div style={{ padding: 24, maxWidth: 640, margin: '0 auto' }}>
      <h5 className='mb-3'>Ödeme Onayı</h5>

      {/*
        Metinler sunucudaki şablon dosyalarından üretiliyor (repo içeriği, kullanıcı girdisi
        değil); değişken alanlar sunucuda HTML-kaçırılarak yerleştiriliyor.
      */}
      <div dangerouslySetInnerHTML={{ __html: view.LongText || '' }} />

      <label className='my-3 d-block'>
        <input
          type='checkbox'
          checked={state.accepted}
          onChange={e => setState({ accepted: e.target.checked })}
        />{' '}
        Yukarıdaki beyanları okudum ve onaylıyorum.
      </label>

      {state.error && <div className='mb-3 text-danger'>{state.error}</div>}

      <button
        type='button'
        className='btn btn-primary'
        disabled={!state.accepted || state.busy}
        onClick={approve}
      >
        ONAYLA VE DOĞRULAMA KODU GÖNDER
      </button>

      {view.KvkkText && (
        <div className='mt-4 pt-3 border-top text-muted' style={{ fontSize: '0.875rem' }}>
          <div dangerouslySetInnerHTML={{ __html: view.KvkkText }} />
        </div>
      )}
    </div>
  )
}

export default CardConsent
